import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApplicationReviewsStore } from 'src/store/application-reviews';
import { useApplicationsStore } from 'src/store/applications';
import { useInsuranceStore } from 'src/store/insurance';

export enum Tier {
  Tier1 = 'Tier 1',
  Tier1_5 = 'Tier 1.5',
  Tier2 = 'Tier 2',
  Tier2_5 = 'Tier 2.5',
  Tier3 = 'Tier 3',
  Tier3_5 = 'Tier 3.5',
  Tier4 = 'Tier 4',
  Tier4_5 = 'Tier 4.5',
  Tier5 = 'Tier 5',
  Tier5_5 = 'Tier 5.5',
  Tier6 = 'Tier 6',
  Tier7 = 'Tier 7',
}


const UnderwritingDetail = () => {
  const { underwritingId } = useParams<{ underwritingId: string }>();
  const navigate = useNavigate();
  const { current, loading, error, loadOne, accept, reject } = useApplicationReviewsStore();
  const { getPayloadPdfUrl } = useApplicationsStore();
  const { plans: allPlans } = useInsuranceStore();

  const [loadingDownloadApp, setLoadingDownloadApp] = useState(false);

  // Review form state
  const [action, setAction] = useState<'accept' | 'reject'>('accept');
  const [note, setNote] = useState('');
  const [tier, setTier] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (underwritingId) {
      loadOne(underwritingId);
    }
  }, [underwritingId, loadOne]);

  const transformApplicationReview = (appReview: any) => {
    if (!appReview) {
      return {
        memberId: '',
        id: '',
        firstName: '',
        lastName: '',
        dateCompleted: '',
        completed: '',
        underwritten: 'Pending',
        by: '',
        status: 0,
        tier: '',
        assignment: 'Pending',
        reason: '',
        application: null,
      };
    }

    const user = appReview.user || {};
    const application = appReview.application || {};
    const payload = application.payload || {};
    const plans = payload.plans || [];
    const firstPlan = plans[0] || {};
    
    return {
      memberId: String(user.id || ''),
      id: String(appReview.id || ''),
      firstName: String(appReview?.application?.payload?.applicants[0]?.firstName || ''),
      lastName: String(appReview?.application?.payload?.applicants[0]?.lastName || ''),
      dateCompleted: appReview.createdAt
        ? new Date(appReview.createdAt).toLocaleDateString()
        : '',
      completed: appReview.updatedAt
        ? new Date(appReview.updatedAt).toLocaleDateString()
        : '',
      underwritten: appReview.status ? 'Completed' : 'Pending',
      by: String(appReview.reviewedBy || ''),
      status: appReview.status,
      tier: firstPlan.id && appReview.priceId 
        ? getPlansTierByPriceId(firstPlan.id, appReview.priceId)
        : '',
      assignment:
        appReview.status === -1
          ? 'Rejected'
          : appReview.status === 1
          ? 'Approved'
          : 'Pending',
      reason: appReview.reason,
      application: appReview.application,
    };
  };

  const getPlansTierByPriceId = (planId: string, priceId: number) => {
    const plan = allPlans.find(p => String(p.id) === planId);
    if (!plan) return '';
    
    const price = plan.prices?.find(p => p.id === priceId);
    return price?.tier || '';
  };

  const handleBackToList = () => {
    navigate('/underwriting');
  };

  const triggerDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleDownloadPHQById = async () => {
    try {
      setLoadingDownloadApp(true);
      if (!current?.application?.id) return;
      const url = await getPayloadPdfUrl(current.application.id);
      if (url) {
        triggerDownload(url, `phq-${current.application.id}.pdf`);
      }
    } catch (err) {
      // noop
    } finally {
      setLoadingDownloadApp(false);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (action === 'accept' && !tier.trim()) return;

    setIsSubmitting(true);
    try {
      if (action === 'accept') {
        await accept(underwritingId!, { tier: tier || '', ...(note ? { note } : {}) });
      } else {
        await reject(underwritingId!, { ...(note ? { note } : {}) });
      }
      navigate('/underwriting');
    } catch (err) {
      console.error('Error submitting review:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (error || !current) {
    return (
      <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
        <div className="text-lg text-red-600">{error || 'Application not found'}</div>
      </div>
    );
  }

  // Additional safety check
  if (!current) {
    return (
      <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
        <div className="text-lg text-red-600">Application not found</div>
      </div>
    );
  }

  const transformedData = transformApplicationReview(current);

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
      {/* Header */}
      <div className="px-8 py-6 flex items-center justify-between max-md:p-2">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-gray-100 flex items-center rounded-full"
          >
            <img src="/icons/left.svg" alt="back" />
          </button>
          <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
            Underwriting Review
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDownloadPHQById}
            disabled={loadingDownloadApp}
            className={`id-card-button-primary px-4 py-2 rounded-full w-full ${
              loadingDownloadApp ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loadingDownloadApp ? 'Downloading…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6 max-md:px-2 space-y-6">
        {/* Application Details */}
        <div className="bg-white-overlay big-corner p-6 max-md:p-4">
          <h3 className="text-lg font-semibold mb-4">Application Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">ID:</span>
                <span>{transformedData.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span>{transformedData.firstName} {transformedData.lastName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date Completed:</span>
                <span>{transformedData.dateCompleted}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  transformedData.underwritten === 'Completed' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {transformedData.underwritten}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Assignment:</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  transformedData.assignment === 'Approved' 
                    ? 'bg-green-100 text-green-800'
                    : transformedData.assignment === 'Rejected'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {transformedData.assignment}
                </span>
              </div>
              {transformedData.tier && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tier:</span>
                  <span className="px-2 py-1 rounded text-sm bg-blue-100 text-blue-800">
                    {transformedData.tier}
                  </span>
                </div>
              )}
              {transformedData.reason && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Reason:</span>
                  <span>{transformedData.reason}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-white-overlay big-corner p-6 max-md:p-4">
          <h3 className="text-lg font-semibold mb-4">Review Application</h3>
          <form onSubmit={handleSubmitReview} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAction('accept')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                  action === 'accept'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => setAction('reject')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                  action === 'reject'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Reject
              </button>
            </div>


            {action === 'accept' && (
              <div>
                <label className="block text-sm font-medium mb-2">Tier *</label>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select tier...</option>
                  {Object.values(Tier).map((tierOption) => (
                    <option key={tierOption} value={tierOption}>
                      {tierOption}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Note</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter note"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
              />
            </div>

            <div className="flex items-center gap-2 w-full">
              <button
                type="button"
                onClick={handleBackToList}
                className="id-card-button-secondary px-4 py-2 rounded-full w-full"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="id-card-button-primary px-4 py-2 rounded-full w-full"
              >
                {isSubmitting ? 'Processing...' : (action === 'accept' ? 'Approve' : 'Reject')}
              </button>
            </div>
          </form>
        </div>
      </div>


    </div>
  );
};

export default UnderwritingDetail;
